import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import type { MayaFormField, MayaFormSpec } from "./types";
import {
  composeSubmission,
  fieldLabel,
  isSingleCheckbox,
  isSingleCheckboxChecked,
} from "./spec";

type FieldValue = string | string[] | boolean;

interface MayaFormProps {
  spec: MayaFormSpec;
  /** Called with the composed "Label: value" message. The caller drops it into
   *  the composer for the user to review/edit/send — this never auto-sends. */
  onSubmit: (text: string, trimmed: boolean) => void;
  onClose: () => void;
  /** Move focus to the first field when the form appears (default). A
   *  function is asked at that moment — e.g. "not while the user is typing
   *  in the composer", where it would steal their keystrokes. */
  autoFocus?: boolean | (() => boolean);
}

const rawPrefill = (f: MayaFormField): string => {
  const v = f.value != null ? f.value : f.default;
  return v != null ? String(v) : "";
};

const seedValue = (f: MayaFormField): FieldValue => {
  const raw = rawPrefill(f);
  const type = (f.type ?? "").toLowerCase();
  if (isSingleCheckbox(f)) return isSingleCheckboxChecked(raw);
  if (type === "checkbox") {
    // multi-select: comma-separated pre-fill, intersected with known options
    const wanted = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return (f.options ?? []).filter((o) => wanted.includes(o));
  }
  return raw;
};

// Resolve a control's state into the string used in the composed message.
const resolveValue = (f: MayaFormField, v: FieldValue): string => {
  if (isSingleCheckbox(f)) return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  return typeof v === "string" ? v.trim() : "";
};

// Shared control chrome, mirrors the Input primitive height/border tokens.
const controlBase =
  "border-border text-foreground flex w-full min-w-0 rounded-sxs border px-3 py-2 text-sm outline-hidden transition-[color,box-shadow] focus-visible:border-primary focus:outline-hidden disabled:opacity-50 placeholder:text-muted-foreground";

export const MayaForm = ({
  spec,
  onSubmit,
  onClose,
  autoFocus = true,
}: MayaFormProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const fields = spec.fields ?? [];
  const containerRef = useRef<HTMLFormElement>(null);

  const [values, setValues] = useState<Record<number, FieldValue>>(() => {
    const init: Record<number, FieldValue> = {};
    fields.forEach((f, i) => {
      init[i] = seedValue(f);
    });
    return init;
  });

  // Re-seed when a fresh spec replaces the card (new turn re-drafts the form).
  useEffect(() => {
    const init: Record<number, FieldValue> = {};
    fields.forEach((f, i) => {
      init[i] = seedValue(f);
    });
    setValues(init);
  }, [spec]);

  // Auto-focus the first control.
  useEffect(() => {
    const wanted = typeof autoFocus === "function" ? autoFocus() : autoFocus;
    if (!wanted) return;
    const el = containerRef.current?.querySelector<HTMLElement>(
      "input, textarea, select",
    );
    el?.focus();
  }, [spec, autoFocus]);

  const setValue = (idx: number, v: FieldValue) =>
    setValues((prev) => ({ ...prev, [idx]: v }));

  const title =
    spec.title?.trim() || t("common:ai_assistant.a_few_details_to_continue");
  const submitLabel = spec.submit?.trim() || t("my-space:ui.send");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const resolved = fields.map((f, i) => resolveValue(f, values[i]));

    // First empty required field blocks submission (single checkbox never does).
    const missingIdx = fields.findIndex(
      (f, i) => f.required && !isSingleCheckbox(f) && !resolved[i],
    );
    if (missingIdx !== -1) {
      toast.error(
        t("common:ai_assistant.please_complete_field", {
          field: fieldLabel(fields[missingIdx], missingIdx),
        }),
      );
      const el =
        containerRef.current?.querySelectorAll<HTMLElement>(
          "[data-mf-field]",
        )?.[missingIdx];
      el?.querySelector<HTMLElement>("input, textarea, select")?.focus();
      return;
    }

    const { text, trimmed } = composeSubmission(spec, resolved);
    onSubmit(text, trimmed);
  };

  return (
    <form
      ref={containerRef}
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        // Escape closes the form — and only the form, not a panel around it.
        if (e.key !== "Escape" || e.defaultPrevented) return;
        e.preventDefault();
        onClose();
      }}
      className="rounded border border-border bg-surface shadow-lg ring-1 ring-foreground/5"
    >
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {spec.description?.trim() && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {spec.description}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t("common:a11y.close_form")}
          title={t("common:a11y.close_form")}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3 px-4 py-3 max-h-[50vh] overflow-y-auto">
        {fields.map((f, i) => (
          <MayaFieldControl
            key={i}
            field={f}
            index={i}
            value={values[i]}
            onChange={(v) => setValue(i, v)}
          />
        ))}
      </div>

      <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          {t("common:buttons.cancel")}
        </Button>
        <Button type="submit" variant="primary" size="sm">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

interface FieldControlProps {
  field: MayaFormField;
  index: number;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
}

const MayaFieldControl = ({
  field,
  index,
  value,
  onChange,
}: FieldControlProps) => {
  const { t } = useTranslation(["common"]);
  const type = (field.type ?? "").toLowerCase();
  const label = fieldLabel(field, index);
  const options = field.options ?? [];
  const placeholder = field.placeholder;
  const fullWidth =
    type === "textarea" || type === "radio" || type === "checkbox";

  // Single on/off toggle — render inline, no separate label row, never required.
  if (isSingleCheckbox(field)) {
    return (
      <label
        data-mf-field
        className="col-span-full flex cursor-pointer items-center gap-2 text-sm text-foreground"
      >
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4 accent-(--color-primary)"
        />
        {label}
      </label>
    );
  }

  const renderControl = () => {
    switch (type) {
      case "textarea":
        return (
          <textarea
            rows={3}
            value={value as string}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={cn(controlBase, "resize-y")}
          />
        );
      case "select":
      case "dropdown":
        return (
          <select
            value={value as string}
            required={field.required}
            onChange={(e) => onChange(e.target.value)}
            className={cn(controlBase, "h-12 bg-surface")}
          >
            <option value="" disabled={field.required} hidden={field.required}>
              {placeholder || t("common:ai_assistant.select_placeholder")}
            </option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      case "radio":
        return (
          <div className="flex flex-wrap gap-2">
            {options.map((o) => {
              const active = value === o;
              return (
                <label
                  key={o}
                  className={cn(
                    "cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                    active
                      ? "border-primary bg-primary-muted text-primary"
                      : "border-border text-foreground hover:bg-muted",
                  )}
                >
                  <input
                    type="radio"
                    name={`mf-${index}`}
                    value={o}
                    checked={active}
                    onChange={() => onChange(o)}
                    className="sr-only"
                  />
                  {o}
                </label>
              );
            })}
          </div>
        );
      case "checkbox": {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div className="flex flex-col gap-1.5">
            {options.map((o) => (
              <label
                key={o}
                className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...selected, o]
                        : selected.filter((s) => s !== o),
                    )
                  }
                  className="size-4 accent-(--color-primary)"
                />
                {o}
              </label>
            ))}
          </div>
        );
      }
      case "password":
        return (
          <Input
            type="password"
            autoComplete="off"
            value={value as string}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            value={value as string}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "date":
        return (
          <Input
            type="date"
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={value as string}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  return (
    <div
      data-mf-field
      className={cn("flex flex-col gap-1.5", fullWidth && "col-span-full")}
    >
      <label className="text-xs font-medium text-muted-foreground">
        {label}
        {field.required && <span className="ms-0.5 text-danger">*</span>}
      </label>
      {renderControl()}
    </div>
  );
};
